// printInvoice.js — Generates a Kamali Gifts Factory shipping invoice in a new print window

import logoImg from '../../assets/image.png';

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

function normalizeAddr(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return null; } }
  if (typeof raw !== 'object') return null;

  // New Address model shape
  if (raw.fullName && raw.street) {
    const lines = [
      raw.street,
      raw.apartment || null,
      [raw.city, raw.state].filter(Boolean).join(', '),
      raw.country || null,
      raw.pincode || null,
    ].filter(Boolean);
    return { name: raw.fullName, phone: raw.phone || '', lines };
  }

  // Legacy shape
  const name = [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() || raw.fullName || '';
  const lines = [
    raw.address || raw.street || '',
    raw.apartment || '',
    [raw.city, raw.state].filter(Boolean).join(', '),
    raw.country || '',
    raw.postCode || raw.pincode || '',
  ].filter(Boolean);
  return { name, phone: raw.phone || '', lines };
}

export function printOrderInvoice(order) {
  const addr = normalizeAddr(order.shippingAddress)
    || normalizeAddr(order.billingAddress)
    || normalizeAddr(typeof order.guestAddress === 'string'
        ? (() => { try { return JSON.parse(order.guestAddress); } catch { return null; } })()
        : order.guestAddress);

  const customerName  = addr?.name || order.customer_name || order.User?.name || '—';
  const customerPhone = addr?.phone || order.customer_phone || order.User?.phone || '—';
  const customerEmail = order.customer_email || order.User?.email || order.guestEmail || '—';
  const addressLines  = addr?.lines || [];

  const orderDate     = fmtDate(order.createdAt);
  const orderRef      = order.referenceSlug || order.id || '—';
  const paymentMethod = (order.paymentMethod || '').toUpperCase() === 'COD'
    ? 'COD (Cash on Delivery)'
    : (order.paymentStatus === 'paid' ? 'PREPAID' : (order.paymentMethod || '—').toUpperCase());

  const totalAmt   = parseFloat(order.totalAmount   || 0);
  const taxAmt     = parseFloat(order.taxAmount     || 0);
  const shipAmt    = parseFloat(order.shippingCharge || 0);
  const discAmt    = parseFloat(order.couponDiscount || 0);
  const subtotal   = totalAmt - shipAmt + discAmt - taxAmt;

  // Shipping details extraction from real order / product data
  let dimensions = order.dimensions || null;
  if (!dimensions && order.shippingDimensions) {
    try {
      const sd = typeof order.shippingDimensions === 'string' ? JSON.parse(order.shippingDimensions) : order.shippingDimensions;
      if (sd?.length && sd?.breadth && sd?.height) {
        dimensions = `${sd.length}x${sd.breadth}x${sd.height}`;
      }
    } catch (e) {}
  }
  if (!dimensions) dimensions = '—';

  let computedWeight = 0;
  (Array.isArray(order.items) ? order.items : []).forEach(it => {
    const w = parseFloat(it.shippingWeight || it.weight || 0);
    computedWeight += w * (parseInt(it.quantity || 1));
  });
  const weightStr = computedWeight > 0
    ? `${computedWeight.toFixed(2)} kg`
    : (order.shippingWeight ? `${parseFloat(order.shippingWeight).toFixed(2)} kg` : '—');

  const routingCode    = order.routingCode || order.shiprocketRoutingCode || (addr?.pincode ? `HUB/${addr.pincode}` : '—');
  const rtoRoutingCode = order.rtoRoutingCode || 'NA';
  const courierService = order.courier || order.courierName || order.service || (order.awbCode ? 'Standard Courier' : '—');
  const awbNumber      = order.awbCode || order.awb || order.trackingNumber || '—';
  const returnPhone    = order.shippedBy || order.returnPhone || '7338814319';
  const customerCare   = order.customerCare || '7338814319';
  const invoiceNumber  = order.invoiceNo
    || (order.referenceSlug ? `INV-${order.referenceSlug}` : `INV-${order.id || ''}`);
  const gstinNumber    = order.gstin || '33CHLPS9107A1ZF';

  // Effective GST rate for per-item breakdown (inclusive)
  const effectiveRate = subtotal > 0 ? (taxAmt / subtotal) * 100 : 0;

  const items = Array.isArray(order.items) ? order.items : [];

  // Per-item row HTML
  const itemRows = items.map((item, i) => {
    const itemTotal  = parseFloat(item.price || 0) * parseInt(item.quantity || 1);
    const gstAmt     = effectiveRate > 0 ? (itemTotal * effectiveRate) / (100 + effectiveRate) : 0;
    const basePrice  = itemTotal - gstAmt;
    const sku        = item.sku || item.variantSku || item.Variant?.sku || item.Product?.sku || (item.selectedVariantId ? `#${item.selectedVariantId}` : '—');
    return `
      <tr>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;">${item.productName || '—'}${item.selectedVariantName ? `<br><span style="font-size:9px;color:#555;">${item.selectedVariantName}</span>` : ''}</td>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;text-align:center;">${sku}</td>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;text-align:center;">${item.quantity || 1}</td>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;text-align:right;">&#8377;${basePrice.toFixed(2)}</td>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;text-align:right;">&#8377;${gstAmt.toFixed(2)}</td>
        <td style="padding:6px 8px;border:1px solid #000;font-size:11px;text-align:right;font-weight:600;">&#8377;${itemTotal.toFixed(2)}</td>
      </tr>`;
  }).join('');

  const totalQty      = items.reduce((s, it) => s + parseInt(it.quantity || 1), 0);
  const totalBaseSum  = subtotal;
  const totalGstSum   = taxAmt;
  const platformFee   = parseFloat(order.platformFee || 0);

  // Dynamic COD / Collectable calculation
  const isCod = (order.paymentMethod || '').toLowerCase().includes('cod');
  const isFullyPaid = order.paymentStatus === 'paid' || order.codCollected;
  let collectableAmt = 0;
  if (isCod && !isFullyPaid) {
    if (order.codAmount !== undefined && order.codAmount !== null) {
      collectableAmt = parseFloat(order.codAmount);
    } else if (order.paymentMethod === 'partial_cod') {
      collectableAmt = Math.max(0, totalAmt - parseFloat(order.advancePaid || 0));
    } else {
      collectableAmt = totalAmt;
    }
  }

  // Logo URL — use the bundled local asset (base64 data URL, always works in print window)
  const logoSrc = logoImg;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Invoice — ${orderRef}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; background: #fff; padding: 24px; }
    .page { max-width: 680px; margin: 0 auto; }

    /* Header */
    .header { text-align: center; margin-bottom: 20px; }
    .header img { height: 130px; object-fit: contain; }

    /* Info table */
    .info-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .info-table td { padding: 4px 8px; border: 1px solid #000; font-size: 11px; vertical-align: top; color: #000; }
    .info-table td:first-child { font-weight: 700; width: 34%; }

    /* Items table */
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .items-table th {
      padding: 6px 8px; border: 1px solid #000;
      background: #fff; font-size: 11px; font-weight: 700; text-align: center; color: #000;
    }
    .items-table td { padding: 5px 8px; border: 1px solid #000; font-size: 11px; color: #000; }
    .items-table tr.total-row td { font-weight: 700; background: #fff; }

    /* Summary table */
    .summary-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .summary-table td { padding: 4px 8px; border: 1px solid #000; font-size: 11px; color: #000; }
    .summary-table td:first-child { font-weight: 700; width: 50%; }

    .footer { text-align: center; font-size: 10px; color: #888; margin-top: 10px; }

    @media print {
      body { padding: 0; }
      @page { margin: 8mm; size: A4; }
      button { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <div class="header">
      <img src="${logoSrc}" alt="Kamali Gifts Factory" />
    </div>

    <!-- Shipping Info -->
    <table class="info-table">
      <tr><td>Ship To</td><td>${customerName}</td></tr>
      <tr><td>Address</td><td>${addressLines.join(', ') || '—'}</td></tr>
      <tr><td>Phone</td><td>${customerPhone}</td></tr>
      <tr><td>Dimensions</td><td>${dimensions}</td></tr>
      <tr><td>Payment</td><td>${paymentMethod}</td></tr>
      <tr><td>Order Total</td><td>&#8377;${totalAmt.toFixed(2)}</td></tr>
      <tr><td>Weight</td><td>${weightStr}</td></tr>
      <tr><td>Routing code</td><td>${routingCode}</td></tr>
      <tr><td>RTO Routing code</td><td>${rtoRoutingCode}</td></tr>
      <tr><td>Service</td><td>${courierService}</td></tr>
      <tr><td>AWB</td><td>${awbNumber}</td></tr>
      <tr><td>Shipped By / Return to</td><td>${returnPhone}</td></tr>
      <tr><td>Customer Care</td><td>${customerCare}</td></tr>
      <tr><td>Customer Email</td><td>${customerEmail}</td></tr>
      <tr><td>Order #</td><td>${orderRef}</td></tr>
      <tr><td>Invoice No.</td><td>${invoiceNumber}</td></tr>
      <tr><td>Invoice Date</td><td>${orderDate}</td></tr>
      <tr><td>Order Date</td><td>${orderDate}</td></tr>
      <tr><td>GSTIN</td><td>${gstinNumber}</td></tr>
    </table>

    <!-- Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th style="text-align:left;">Item</th>
          <th>SKU</th>
          <th>Qty</th>
          <th>Product Cost</th>
          <th>GST</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        <tr class="total-row">
          <td><strong>Total</strong></td>
          <td></td>
          <td style="text-align:center;">${totalQty}</td>
          <td style="text-align:right;">&#8377;${totalBaseSum.toFixed(2)}</td>
          <td style="text-align:right;">&#8377;${totalGstSum.toFixed(2)}</td>
          <td style="text-align:right;"> &#8377;${totalAmt.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>

    <!-- Summary -->
    <table class="summary-table">
      <tr><td>Platform Fee</td><td>&#8377;${platformFee > 0 ? platformFee.toFixed(2) : '0'}</td></tr>
      <tr><td>Shipping Charges</td><td>&#8377; ${shipAmt.toFixed(2)}</td></tr>
      <tr><td>Discount</td><td>${discAmt > 0 ? '&#8377; ' + discAmt.toFixed(2) : '&#8377;0'}</td></tr>
      <tr><td>Collectable Amount</td><td>&#8377;${collectableAmt.toFixed(2)}</td></tr>
    </table>

    <div class="footer">This is a computer-generated invoice. No signature required.</div>

    <!-- Print button (hidden on print) -->
    <div style="text-align:center;margin-top:20px;">
      <button onclick="window.print()" style="
        padding:10px 28px;background:#c0185c;color:#fff;border:none;
        border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;
      ">🖨️ Print / Save PDF</button>
    </div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=780,height=900');
  if (!win) { alert('Pop-up blocked. Please allow pop-ups for this site.'); return; }
  win.document.write(html);
  win.document.close();
}
